import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty, uniqBy } from 'lodash';
import { useLocation } from 'react-router-dom';
import { usePrevious } from 'react-use';

import useFabricFavs from 'data-hooks/useFabricFavs';
import { getPDPCustomizerURLParams } from 'components/consumer/PDP/helpers/pdpQueryParamUtils';
import usePDPOrderEdit from 'data-hooks/usePDPOrderEdit';
import useFlashSaleList from 'hooks/useFlashSaleList';
import usePDPOptionsClearanceData from 'data-hooks/useClearanceData/usePDPOptionsClearanceData';
import usePostHogFeatureFlag from 'hooks/usePostHogFeatureFlag';

import {
  PDP_READYTOSHIP_TAB_ID,
  PDP_FILTER_TYPES,
  GREENGUARD_FABRICS,
  PDP_CUSTOMIZE_TAB_ID,
} from 'commons/constants';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import usePdpVariantSkuOverride from 'components/consumer/PDP/state/context/usePdpVariantSkuOverride';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import trimPopularMaterialsArray from './trimPopularMaterialsArray';
import moveMaterialToStart from './moveMaterialToStart';

const usePDPFabricFilters = (slug, isEditOrder, hookOptions = {}) => {
  const { target = 'default' } = hookOptions;
  const location = useLocation();
  const queryString = location.search;

  const pdpFabricFiltersFeatureFlag = usePostHogFeatureFlag('PDPFabricFilters');

  const isMultiSelectFilters =
    pdpFabricFiltersFeatureFlag === 'test' || isNewConsumerApp;

  const {
    loading,
    error,
    data,
    actions: { onOptionValueHover, onOptionValueSelection },
  } = usePDPOptions();

  const {
    actions: { setOptionAsFav, removeOptionAsFav } = {},
    data: { userFabricFavs: favMaterialsObj, fabricFavsInfo } = {},
  } = useFabricFavs({ loadMaterialDetails: true });

  const {
    products: flashSaleProducts,
    variations: flashSaleVariations,
  } = useFlashSaleList();

  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();
  const pdpVariantSkuOverride = usePdpVariantSkuOverride();

  const {
    options,
    pdpHasUserSelectedOptions,
    productId,
    selectedOptions,
    selectedOptionsFromURLOverride,
    stock,
    upholsteryOptionId,
  } = data || {};

  // If filterBy=all is in the URL then initially show all materials, otherwise
  // initially show popular materials
  const initialFilterType = useMemo(() => {
    const queryStringParams = new URLSearchParams(queryString);

    return queryStringParams.get('filterBy') === 'all'
      ? PDP_FILTER_TYPES.ALL
      : PDP_FILTER_TYPES.POPULAR;
  }, [queryString]);

  const [filterType, setFilterType] = useState(initialFilterType);

  const [filterTypeArray, setFilterTypeArray] = useState([
    PDP_FILTER_TYPES.POPULAR,
  ]);

  const defaultFilterIsSetRef = useRef(false);

  // for color filter (single color)
  const [selectedColor, setSelectedColor] = useState(null);

  // for color filter (multiple colors, for multi-select experiment)
  const [selectedColorArray, setSelectedColorArray] = useState([]);

  // Set filter type array (for multi-select experiment)
  const setFilterTypeMultiSelect = useCallback(value => {
    if (value === PDP_FILTER_TYPES.CLEARALL) {
      setFilterTypeArray([PDP_FILTER_TYPES.ALL]);
      setSelectedColorArray([]);
      return;
    }

    setFilterTypeArray(prev => {
      // When a non-"Clear All" button is clicked and the "All" filter is
      // already enabled, unselect every existing filter and only mark the new
      // filter as selected
      if (prev?.includes('all')) {
        return [value];
      }

      if (prev?.includes(value)) {
        const newValues = prev?.filter(v => v !== value);
        // When unselecting a filter, if there are no other filters marked as
        // selected then mark the "All" filter as selected
        return newValues?.length === 0
          ? ['all']
          : prev?.filter(v => v !== value);
      }

      return [...prev, value];
    });
  }, []);

  const setSelectedColorMultiSelect = useCallback(color => {
    setSelectedColorArray(prev => {
      if (prev?.includes(color)) {
        return prev?.filter(v => v !== color);
      }
      return [...prev, color];
    });
  }, []);

  const { data: orderEditData = {} } = usePDPOrderEdit({
    skip: !isEditOrder,
  });

  // for clearance materials
  const { data: productClearanceData } = usePDPOptionsClearanceData(productId);

  const editOrderDiscount = orderEditData?.cartItem?.discount_percent; // this can become problematic in some edge cases

  const formatOptionValue = useCallback(
    ({
      additional_cost: additionalCost,
      color,
      description,
      exclude_from_sale: excludeFromSale,
      group,
      id,
      image,
      is_active: isActive,
      performance_fabric: performanceFabric,
      pet_friendly: petFriendly,
      popular,
      popularOrder,
      sku,
      sprite_config: spriteConfig,
      sustainable_fabric: sustainableFabric,
      tier,
      value: label,
    }) => {
      const groupName = group?.name || '';
      const velvet = group?.material === 'velvet';

      const greenguardFabric =
        group?.material === 'greenguard' ||
        (groupName ? GREENGUARD_FABRICS.includes(groupName) : false);

      return {
        additionalCost:
          isEditOrder &&
          !excludeFromSale &&
          additionalCost > 0 &&
          editOrderDiscount > 0
            ? Math.round(additionalCost * ((100 - editOrderDiscount) / 100))
            : additionalCost,
        cleaningCode: group?.cleaning_code,
        color,
        description,
        exclude_from_sale: excludeFromSale,
        greenguard_fabric: greenguardFabric,
        groupMaterial: group?.material,
        groupName,
        image,
        is_active: isActive,
        label,
        materialId: id,
        origAddlCost: additionalCost,
        performance_fabric: performanceFabric,
        pet_friendly: petFriendly,
        popular,
        popularOrder,
        sku,
        spriteConfig,
        sustainable_fabric: sustainableFabric,
        tier,
        upholsteryOptionId,
        velvet,
      };
    },
    [editOrderDiscount, isEditOrder, upholsteryOptionId]
  );

  // optimize calls
  const clearanceFabricsSKU = useMemo(
    () => Object.keys(productClearanceData?.optionsClearanceData || {}),
    [productClearanceData?.optionsClearanceData]
  );

  const isReadyToShipTab = pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID;
  const finalOptions = isReadyToShipTab ? stock?.stockOptions : options;

  const upholsteryOption = finalOptions?.find(
    ({ id }) => id === upholsteryOptionId
  );

  const upholsteryOptionTypeName = upholsteryOption?.name || 'upholstery';

  const allMaterials = useMemo(() => {
    if (!upholsteryOption) {
      return [];
    }

    const updatedAllMaterials = (upholsteryOption?.values || [])
      .filter(
        optionValue =>
          (!isReadyToShipTab && optionValue.is_active) || isReadyToShipTab
      )
      .map(optionValue => formatOptionValue(optionValue));

    return updatedAllMaterials;
  }, [formatOptionValue, isReadyToShipTab, upholsteryOption]);

  const favoriteMaterials = useMemo(() => {
    if (!allMaterials) {
      return null;
    }

    const allFavoriteMaterials = allMaterials.filter(
      material => favMaterialsObj?.[material.materialId]
    );

    const favMaterialIds = allFavoriteMaterials.reduce(
      (acc, material) => ({ ...acc, [material.materialId]: true }),
      {}
    );

    const updatedFavoriteMaterials = [...allFavoriteMaterials];

    // CON-3009 - Get details of fav fabrics not applicable to product
    const favMaterialsNotApplicableToProduct = Object.keys(
      favMaterialsObj || {}
    ).filter(materialId => !favMaterialIds[materialId]);

    if (
      favMaterialsNotApplicableToProduct?.length > 0 &&
      !isEmpty(fabricFavsInfo)
    ) {
      favMaterialsNotApplicableToProduct.forEach(materialId => {
        const materialDetail = fabricFavsInfo[materialId];

        if (materialDetail) {
          updatedFavoriteMaterials.push({
            ...materialDetail,
            applicableForProduct: false,
            label: materialDetail.value?.trim(),
            groupMaterial: materialDetail.group?.material,
            cleaningCode: materialDetail.group?.cleaning_code,
            pet_friendly: materialDetail.group?.pet_friendly,
            velvet: materialDetail.group?.material === 'velvet',
            greenguard_fabric:
              materialDetail.group?.material === 'greenguard' ||
              GREENGUARD_FABRICS.includes(materialDetail.group?.name),
            materialId: parseInt(materialId, 10),
          });
        }
      });
    }

    return updatedFavoriteMaterials;
  }, [allMaterials, fabricFavsInfo, favMaterialsObj]);

  const allInStockValues = useMemo(() => {
    if (!stock?.stockOptions?.length || !upholsteryOptionId) {
      return null;
    }

    // Find first in stock value
    const inStockOption = stock?.stockOptions.find(
      ({ id }) => id === upholsteryOptionId
    );

    const updatedAllInStockValues = inStockOption ? inStockOption.values : null;

    return updatedAllInStockValues;
  }, [stock?.stockOptions, upholsteryOptionId]);

  const instockOptionValueIds = useMemo(() => {
    const updatedInstockOptionValueIds = [];

    allInStockValues?.forEach(instockVal => {
      if (instockVal?.is_active) {
        updatedInstockOptionValueIds.push(instockVal.id);
      }
    });

    return updatedInstockOptionValueIds;
  }, [allInStockValues]);

  const inStockOptions = useMemo(() => {
    const updatedInStockOptions = allInStockValues
      ? allInStockValues
          .map(instockVal => {
            if (!instockVal.is_active) {
              return null;
            }

            return {
              materialId: instockVal.id,
              label: instockVal.value,
              image: instockVal.image,
              spriteConfig: instockVal.sprite_config,
              upholsteryOptionId,
              description: instockVal.description,
              groupName: instockVal.group?.name || '',
              tier: instockVal.tier,
              color: instockVal.color,
              popular: instockVal.popular,
              pet_friendly: instockVal.pet_friendly,
              exclude_from_sale: instockVal.exclude_from_sale,
              performance_fabric: instockVal.performance_fabric,
              sustainable_fabric: instockVal.sustainable_fabric,
              sku: instockVal.sku,
              additionalCost:
                isEditOrder &&
                !instockVal.exclude_from_sale &&
                instockVal.additional_cost > 0 &&
                editOrderDiscount > 0
                  ? Math.round(
                      instockVal.additional_cost *
                        ((100 - editOrderDiscount) / 100)
                    )
                  : instockVal.additional_cost,
              groupMaterial: instockVal.group?.material,
              cleaningCode: instockVal.group?.cleaning_code,
              origAddlCost: instockVal.additional_cost,
            };
          })
          .filter(v => !!v)
      : null;

    return updatedInStockOptions;
  }, [allInStockValues, editOrderDiscount, isEditOrder, upholsteryOptionId]);

  // Find selected material
  const selectedMaterial = useMemo(() => {
    if (!selectedOptions) return {};

    const selectedMaterialId = selectedOptions[upholsteryOptionId];

    const updatedSelectedMaterial = allMaterials.find(
      ({ materialId }) => materialId === selectedMaterialId
    );

    return updatedSelectedMaterial || {};
  }, [allMaterials, selectedOptions, upholsteryOptionId]);

  // Set the initial selected material using useRef so that it is only set
  // during first render.
  const initialSelectedMaterialRef = useRef(selectedMaterial);

  const isProductInFlashSale = flashSaleProducts?.includes(productId);

  const flashSaleVariation = useMemo(
    () => (isProductInFlashSale ? flashSaleVariations?.[productId] : {}),
    [flashSaleVariations, isProductInFlashSale, productId]
  );

  const flashSaleMaterials = useMemo(
    () =>
      isProductInFlashSale
        ? allMaterials.filter(val =>
            flashSaleVariation[upholsteryOptionId]?.includes(val.materialId)
          )
        : [],
    [allMaterials, flashSaleVariation, isProductInFlashSale, upholsteryOptionId]
  );

  // Derive all materials marked as popular in API response
  const allPopularMaterials = useMemo(() => {
    const allPopularMaterialsInOrder = allMaterials
      .filter(
        material =>
          material.popular &&
          !flashSaleVariation[upholsteryOptionId]?.includes(material.materialId)
      )
      .sort((a, b) => a.popularOrder - b.popularOrder);

    // If there are no materials marked as popular, then use all materials
    return allPopularMaterialsInOrder.length
      ? allPopularMaterialsInOrder
      : allMaterials;
  }, [allMaterials, flashSaleVariation, upholsteryOptionId]);

  // Show any flash sale materials first in the popular materials group
  const popularMaterialsFlashSalesFirst = useMemo(() => {
    const allPopularMaterialsWithFlashSales = [
      ...flashSaleMaterials,
      ...allPopularMaterials,
    ];

    return trimPopularMaterialsArray(allPopularMaterialsWithFlashSales);
  }, [allPopularMaterials, flashSaleMaterials]);

  // Show flash sales and in stock materials first in the popular group
  const popularMaterialsFlashSalesAndInStockFirst = useMemo(() => {
    const allStockOptionsPresent =
      !!inStockOptions?.length &&
      !selectedOptionsFromURLOverride &&
      !!inStockOptions.every(
        (stockedOption, idx) =>
          popularMaterialsFlashSalesFirst?.[idx] === stockedOption.materialId
      );

    if (inStockOptions?.length && !selectedOptionsFromURLOverride) {
      if (allStockOptionsPresent) {
        return popularMaterialsFlashSalesFirst;
      }

      const flashSaleInStockAndPopularMaterials = [
        ...(flashSaleMaterials || []),
        ...inStockOptions.filter(
          inStockVal =>
            !(flashSaleVariation[upholsteryOptionId] || []).includes(
              inStockVal.materialId
            )
        ),
        ...popularMaterialsFlashSalesFirst.filter(
          val =>
            !instockOptionValueIds.includes(val.materialId) &&
            !(flashSaleVariation[upholsteryOptionId] || []).includes(
              val.materialId
            )
        ),
      ];

      return trimPopularMaterialsArray(flashSaleInStockAndPopularMaterials);
    }

    return popularMaterialsFlashSalesFirst;
  }, [
    flashSaleMaterials,
    flashSaleVariation,
    inStockOptions,
    instockOptionValueIds,
    popularMaterialsFlashSalesFirst,
    selectedOptionsFromURLOverride,
    upholsteryOptionId,
  ]);

  // If the user selects a material while filtering by "All", save this material
  // to show it first when the user switches back to "Popular" filter
  const [lastSelectedMaterialInAll, setLastSelectedMaterialInAll] = useState(
    null
  );

  useEffect(() => {
    if (filterType === PDP_FILTER_TYPES.ALL) {
      setLastSelectedMaterialInAll(selectedMaterial);
    }
  }, [filterType, selectedMaterial]);

  // Derive final popular materials array
  const popularMaterials = useMemo(() => {
    // If an initial material is selected (e.g. when fabric is set via query
    // string, or pdpVariantSkuOverride is set for QuickViewModal), move the
    // initial selected material to the start of the popular materials array
    const initialMaterialFirst = moveMaterialToStart(
      popularMaterialsFlashSalesAndInStockFirst,
      initialSelectedMaterialRef.current
    );

    const trimmedInitialMaterialFirst = trimPopularMaterialsArray(
      initialMaterialFirst
    );

    const isSelectedMaterialInArray = trimmedInitialMaterialFirst.some(
      material => material.materialId === lastSelectedMaterialInAll?.materialId
    );

    // If the user has selected a material while filtering by "All", and it is
    // not already in the popular materials array, move it to the start
    const lastSelectedMaterialInAllFirst =
      lastSelectedMaterialInAll && !isSelectedMaterialInArray
        ? moveMaterialToStart(initialMaterialFirst, lastSelectedMaterialInAll)
        : initialMaterialFirst;

    return trimPopularMaterialsArray(lastSelectedMaterialInAllFirst);
  }, [lastSelectedMaterialInAll, popularMaterialsFlashSalesAndInStockFirst]);

  useEffect(() => {
    if (
      !stock?.stockOptions?.length ||
      !popularMaterials?.length ||
      !upholsteryOptionId
    ) {
      return;
    }

    if (
      selectedOptions?.[upholsteryOptionId] &&
      inStockOptions?.[0]?.materialId &&
      selectedOptions?.[upholsteryOptionId] !==
        inStockOptions?.[0]?.materialId &&
      !flashSaleVariation[upholsteryOptionId]?.length &&
      !pdpHasUserSelectedOptions &&
      !getPDPCustomizerURLParams(window.location) &&
      !pdpVariantSkuOverride &&
      target === 'default'
    ) {
      onOptionValueSelection(
        upholsteryOptionId,
        inStockOptions[0].materialId,
        null,
        false
      );
    }
  }, [
    flashSaleVariation,
    inStockOptions,
    onOptionValueSelection,
    pdpHasUserSelectedOptions,
    pdpVariantSkuOverride,
    popularMaterials?.length,
    selectedOptions,
    stock?.stockOptions?.length,
    target,
    upholsteryOptionId,
  ]);

  // update filtered materials
  const filteredMaterials = useMemo(() => {
    let results = [];

    const isNotLeatherAndReadyToShip =
      pdpSelectedCustomizerTab !== PDP_READYTOSHIP_TAB_ID &&
      upholsteryOptionTypeName !== 'Leather';

    const filterConditions = {
      [PDP_FILTER_TYPES.FAVORITES]: favoriteMaterials,
      [PDP_FILTER_TYPES.POPULAR]: popularMaterials.length
        ? popularMaterials
        : null,
      [PDP_FILTER_TYPES.VELVET]: v => v.velvet,
      [PDP_FILTER_TYPES.GREENGUARD]: v => v.greenguard_fabric,
      [PDP_FILTER_TYPES.PETFRIENDLY]: v => v.pet_friendly,
      [PDP_FILTER_TYPES.SAFEGUARD]: v => v.performance_fabric,
      [PDP_FILTER_TYPES.SUSTAINABLE]: v => v.sustainable_fabric,
      [PDP_FILTER_TYPES.CLEARANCE]: v => clearanceFabricsSKU.includes(v.sku),
      [PDP_FILTER_TYPES.ALL]: v =>
        v.color && selectedColor
          ? v.color.toLowerCase() === selectedColor
          : true,
    };

    const selectedFilters = [];

    if (isNotLeatherAndReadyToShip) {
      if (isMultiSelectFilters) {
        filterTypeArray.forEach(currentFilterType => {
          const condition = filterConditions[currentFilterType];

          if (Array.isArray(condition)) {
            selectedFilters.push(condition);
          } else if (typeof condition === 'function') {
            const currentFilteredMaterials = allMaterials.filter(condition);

            if (Array.isArray(currentFilteredMaterials)) {
              selectedFilters.push(currentFilteredMaterials);
            }
          }
        });

        const sets = selectedFilters.map(arr =>
          Array.isArray(arr)
            ? new Set(arr.map(item => item.materialId))
            : new Set()
        );

        if (sets[0]) {
          const intersection = Array.from(sets[0]).filter(materialId =>
            sets.every(set => set.has(materialId))
          );

          if (intersection.length > 0) {
            const commonMaterial = selectedFilters.reduce(
              (result, arr) =>
                result.concat(
                  arr.filter(item => intersection.includes(item.materialId))
                ),
              []
            );

            results = commonMaterial;
          } else {
            results = [];
          }

          results = uniqBy(results, 'materialId');
        } else {
          results = [];
        }
      } else {
        const condition = filterConditions[filterType];

        if (Array.isArray(condition)) {
          results = [...condition];
        } else if (typeof condition === 'function') {
          results = allMaterials.filter(condition);
        }
      }
    } else {
      results = Array.isArray(allMaterials) ? allMaterials.filter(Boolean) : [];
    }

    if (isMultiSelectFilters && selectedColorArray.length) {
      results = results.filter(
        material =>
          material.color &&
          selectedColorArray.includes(material.color.toLowerCase())
      );
    } else if (selectedColor) {
      results = results.filter(
        material =>
          material.color && material.color.toLowerCase() === selectedColor
      );
    }

    return results;
  }, [
    allMaterials,
    clearanceFabricsSKU,
    favoriteMaterials,
    filterType,
    filterTypeArray,
    isMultiSelectFilters,
    pdpSelectedCustomizerTab,
    popularMaterials,
    selectedColor,
    selectedColorArray,
    upholsteryOptionTypeName,
  ]);

  const firstFilteredMaterialId = useMemo(
    () => filteredMaterials[0]?.materialId,
    [filteredMaterials]
  );

  useEffect(() => {
    if (!isReadyToShipTab || !filteredMaterials?.length) return;

    // Check if the selected material is in the list of filtered materials
    const isMaterialInFilteredMaterials = Boolean(
      filteredMaterials.find(
        material => material?.materialId === selectedMaterial?.materialId
      )
    );

    if (!isMaterialInFilteredMaterials) {
      onOptionValueSelection(
        upholsteryOptionId,
        firstFilteredMaterialId,
        null,
        false
      );
    }
  }, [
    filteredMaterials,
    firstFilteredMaterialId,
    isReadyToShipTab,
    onOptionValueSelection,
    selectedMaterial,
    upholsteryOptionId,
  ]);

  const previousTab = usePrevious(pdpSelectedCustomizerTab);

  // This  is used when user switch back to CUSTOMIZE tab from RTS the popular is not selected.
  // Taking track of previous tab cause without it, it will not allowing to de-select every flags.
  useEffect(() => {
    if (
      previousTab === PDP_READYTOSHIP_TAB_ID &&
      pdpSelectedCustomizerTab === PDP_CUSTOMIZE_TAB_ID &&
      isMultiSelectFilters &&
      filterTypeArray.includes(PDP_FILTER_TYPES.ALL)
    ) {
      setFilterTypeArray([PDP_FILTER_TYPES.POPULAR]);
    }
  }, [
    filterTypeArray,
    isMultiSelectFilters,
    pdpSelectedCustomizerTab,
    previousTab,
  ]);

  // derive disabled materials
  const disabledMaterialIds = useMemo(() => {
    const { stockOptionsDisabledStatus } = stock || {};

    return isReadyToShipTab && allMaterials
      ? allMaterials
          .filter(material => stockOptionsDisabledStatus?.[material.materialId])
          .map(material => material.materialId)
      : [];
  }, [allMaterials, isReadyToShipTab, stock]);

  const filterCounts = useMemo(
    () => ({
      [PDP_FILTER_TYPES.ALL]: allMaterials?.length || 0,
      [PDP_FILTER_TYPES.POPULAR]: popularMaterials?.length || 0,
      [PDP_FILTER_TYPES.PETFRIENDLY]:
        allMaterials?.filter(material => material.pet_friendly)?.length || 0,
      [PDP_FILTER_TYPES.SAFEGUARD]:
        allMaterials?.filter(material => material.performance_fabric)?.length ||
        0,
      [PDP_FILTER_TYPES.SUSTAINABLE]:
        allMaterials?.filter(material => material.sustainable_fabric)?.length ||
        0,
      [PDP_FILTER_TYPES.VELVET]:
        allMaterials?.filter(material => material.velvet)?.length || 0,
      [PDP_FILTER_TYPES.FAVORITES]:
        Object.keys(favMaterialsObj || {}).length || 0,
      [PDP_FILTER_TYPES.GREENGUARD]:
        allMaterials?.filter(
          material =>
            material.groupMaterial === 'greenguard' ||
            GREENGUARD_FABRICS.includes(material.groupName)
        )?.length || 0,
    }),
    [allMaterials, popularMaterials, favMaterialsObj]
  );

  // set default to all if the total count is less than 7
  useEffect(() => {
    if (
      filterCounts[PDP_FILTER_TYPES.ALL] &&
      filterCounts[PDP_FILTER_TYPES.ALL] < 7 &&
      !defaultFilterIsSetRef.current
    ) {
      if (isMultiSelectFilters) {
        setFilterTypeArray([PDP_FILTER_TYPES.ALL]);
      } else {
        setFilterType(PDP_FILTER_TYPES.ALL);
      }

      defaultFilterIsSetRef.current = true;
    }
  }, [filterCounts, isMultiSelectFilters]);

  const flashSaleOptionIds = useMemo(
    () => flashSaleMaterials.map(material => material.materialId),
    [flashSaleMaterials]
  );

  return {
    loading,
    error,
    data: {
      optionName: upholsteryOptionTypeName,
      filterType: isMultiSelectFilters ? filterTypeArray : filterType,
      allMaterials,
      selectedMaterial,
      filteredMaterials,
      disabledMaterialIds,
      selectedColor: isMultiSelectFilters ? selectedColorArray : selectedColor,
      upholsteryOptionId,
      isSelectedOptionsQuickship: stock?.isSelectedOptionsQuickship,
      selectedOptionsStockQty: stock?.selectedOptionsStockQty,
      selectionValidity: stock?.selectionValidity,
      allMaterialsLength: allMaterials.length,
      filterCounts,
      favMaterials: favMaterialsObj,
      flashSaleOptionIds,
      isClearanceFabricAvailable: clearanceFabricsSKU?.length > 0,
    },
    actions: {
      setFilterType: isMultiSelectFilters
        ? setFilterTypeMultiSelect
        : setFilterType,
      setSelectedColor: isMultiSelectFilters
        ? setSelectedColorMultiSelect
        : setSelectedColor,
      onOptionValueSelection,
      onOptionValueHover,
      setOptionAsFav,
      removeOptionAsFav,
    },
  };
};

export default usePDPFabricFilters;
